import BigNumber from 'bignumber.js'

import { pipe, Record } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import {
  InviteCode,
  ReferralCode,
} from '@root/entities/referral/model/referral-code'
import { UserShort } from '@root/entities/user-short'

import * as BestDeal from './best-deal'
import * as ProfileWallet from './profile-wallet'

import type { AmountPair } from '@root/entities/amount-pair'
import type { UserApi } from '@root/shared/api'
import type { AvatarsHost, TokenLogosHost } from '@root/shared/models'

export interface ReferralInfo {
  code: ReferralCode | undefined
  referralsCount: number
  referralBalanceNative: BigNumber
  referralBalanceUsd: BigNumber
  /**
   * Code which user used to join the app
   */
  inviteCode: InviteCode | undefined
}

export interface Profile extends UserShort.UserShort {
  referralRank: number | undefined
  tradeRank: number | undefined
  trading: TradingInfo
  referralInfo: Record<Network.Network, ReferralInfo>
  bestDeal: BestDeal.BestDeal | undefined

  deployedTokensCount: number
  followers: ReadonlyArray<unknown>
  following: ReadonlyArray<unknown>
  likesCount: number
  mentionsCount: number
  wallets: ReadonlyArray<ProfileWallet.ProfileWallet>
}

export interface TradingInfo {
  tradingSummary: AmountPair.AmountPair
  profit: AmountPair.AmountPair
  loss: AmountPair.AmountPair
  pnl: AmountPair.AmountPair
}

export const updateReferralCode = (args: {
  profile: Profile
  network: Network.Network
  referralCode: ReferralCode
}): Profile => {
  return {
    ...args.profile,
    referralInfo: pipe(
      args.profile.referralInfo,
      Record.set(args.network, {
        ...args.profile.referralInfo[args.network],
        code: args.referralCode,
      }),
    ),
  }
}

export const fromDto = (
  dto: UserApi.UserProfileDto,
  avatarsHost: AvatarsHost,
  tokenLogosHost: TokenLogosHost,
): Profile => {
  const toPair = (
    dto: UserApi.UserProfileDto['trading']['pnl'],
  ): AmountPair.AmountPair => {
    return {
      native: BigNumber(dto.native),
      usd: BigNumber(dto.usd),
    }
  }
  const bestDeal = dto.bestDealDetails
    ? BestDeal.fromDto(dto.bestDealDetails, tokenLogosHost)
    : undefined

  const mapReferralInfo = (
    dto: UserApi.UserProfileReferralInfoDto,
  ): ReferralInfo => {
    return {
      code: ReferralCode(dto.code),
      referralBalanceNative: BigNumber(dto.referralBalanceNative),
      referralBalanceUsd: BigNumber(dto.referralBalanceUSD),
      referralsCount: dto.referralsCount,
      inviteCode: dto.usedReferralCode
        ? InviteCode(dto.usedReferralCode)
        : undefined,
    }
  }

  return {
    ...UserShort.fromDto(dto, avatarsHost),
    referralRank: dto.referralRank,
    tradeRank: dto.tradeRank,
    trading: {
      loss: toPair(dto.trading.lose),
      pnl: toPair(dto.trading.pnl),
      profit: toPair(dto.trading.profit),
      tradingSummary: toPair(dto.trading.tradingSummary),
    },
    wallets: dto.wallets.map((wallet) => ProfileWallet.fromDto(wallet)),
    referralInfo: {
      [Network.Solana]: mapReferralInfo(dto.referralSolInfo),
      [Network.Ethereum]: mapReferralInfo(dto.referralEthInfo),
      // TODO: BNB Temp
      [Network.Bsc]: mapReferralInfo(dto.referralEthInfo),
      // TODO: Wait for BE to support referral info for Ton and Base
      [Network.Ton]: {
        code: ReferralCode(''),
        referralBalanceNative: BigNumber(0),
        referralBalanceUsd: BigNumber(0),
        referralsCount: 0,
        inviteCode: undefined,
      },
      [Network.Base]: {
        code: ReferralCode(''),
        referralBalanceNative: BigNumber(0),
        referralBalanceUsd: BigNumber(0),
        referralsCount: 0,
        inviteCode: undefined,
      },
    },
    bestDeal,
    deployedTokensCount: dto.deployedTokensCount,
    followers: dto.followers,
    following: dto.following,
    likesCount: dto.likesCount,
    mentionsCount: dto.mentionsCount,
  }
}
